import { useIsFirstRender } from '@mantine/hooks';
import type { MailComposerSource } from 'modules/Mails/types';
import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { captureEvent } from 'shared/analytics/capture-event';
import type { SendEmail } from 'shared/hooks/use-send-email';
import type { ApiError } from 'shared/hooks/utils/types';
import { useCreateDraft } from './use-create-draft';
import { useUpdateDraft } from './use-update-draft';

const DEBOUNCE_MS = 1500;

type UseSaveDraftOptions = {
  source: MailComposerSource;
  threadId: string | undefined;
  draftMailId: string | null;
  formValues: SendEmail;
  defaultBody?: string;
  isSending: boolean;
  onDraftCreated?: (result: { id: string; threadId: string }) => void;
  onDraftMissing?: () => void;
};

export function useSaveDraft({
  source,
  threadId,
  draftMailId,
  formValues,
  defaultBody = '',
  isSending,
  onDraftCreated,
  onDraftMissing,
}: UseSaveDraftOptions) {
  const [isSavedAsDraft, setIsSavedAsDraft] = useState(false);

  const isFirstRender = useIsFirstRender();
  const draftMailIdRef = useRef(draftMailId);
  draftMailIdRef.current = draftMailId;
  const onDraftCreatedRef = useRef(onDraftCreated);
  onDraftCreatedRef.current = onDraftCreated;
  const onDraftMissingRef = useRef(onDraftMissing);
  onDraftMissingRef.current = onDraftMissing;

  const { createDraft, isCreateDraftLoading } = useCreateDraft();
  const { updateDraft, isUpdateDraftLoading } = useUpdateDraft();

  const { body, subject, toMail, ccMail, bccMail } = formValues;

  useEffect(() => {
    if (isFirstRender || isSending) return;

    setIsSavedAsDraft(false);

    if (subject.trim() === '') return;

    const handleSavedAsDraft = () => setIsSavedAsDraft(true);

    const timer = setTimeout(() => {
      if (draftMailIdRef.current && threadId) {
        updateDraft(
          { draftId: draftMailIdRef.current, threadId, subject, body, toMail, ccMail, bccMail },
          {
            onSuccess: handleSavedAsDraft,
            onError: (error: ApiError) => {
              if (error.response?.status === 404) onDraftMissingRef.current?.();
            },
          },
        );
      } else {
        if (body === defaultBody) return;
        createDraft(
          { threadId, subject, body, toMail, ccMail, bccMail },
          {
            onSuccess: result => {
              handleSavedAsDraft();
              captureEvent(posthog, 'mail:draft_created', { source });
              onDraftCreatedRef.current?.(result);
            },
          },
        );
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    body,
    subject,
    toMail,
    ccMail,
    bccMail,
    threadId,
    isFirstRender,
    isSending,
    source,
    updateDraft,
    createDraft,
    defaultBody,
  ]);

  return { isSavedAsDraft, isSaveDraftLoading: isCreateDraftLoading || isUpdateDraftLoading };
}
